/**
 * Route layout metadata (Enterprise Shell — ADR-0020).
 * Single source for scroll/fit shell classes; replaces pathname strings in Layout1.
 */

const DOCUMENT_SCROLL_PREFIXES = [
  '/customers',
  '/payment-report',
  '/payments',
  '/expenses',
  '/reports',
  '/settings',
  '/sales',
  '/purchases',
  '/orders',
  '/suppliers',
  '/inventory',
  '/stock',
  '/inventur',
  '/mitarbeiter',
];

/** Manager list/overview routes where ContextToolbar / familyHeader owns the page title (Shell Spec §1.4). */
const CONTEXT_TOOLBAR_TITLE_PREFIXES = [
  '/customers',
  '/orders',
  '/suppliers',
  '/sales',
  '/expenses',
  '/purchases',
  '/payments',
  '/payment-report',
  '/inventory',
  '/stock',
  '/inventur',
  '/reports',
  '/restaurant',
  '/settings',
  '/mitarbeiter',
];

function matchesPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * @param {string} pathname
 * @param {'manager'|'worker'|string|undefined} roleType
 * @param {{ businessTypeId?: string }=} options
 */
export function resolveRouteLayoutMeta(pathname, roleType, options = {}) {
  const path = pathname || '/';
  /** Bare shell: cuisine KDS (live restaurant POS uses fitPos + sidebar). */
  const kioskShell = matchesPrefix(path, '/cuisine');
  const fitShell = path === '/' && !kioskShell;
  const fitPos = fitShell && roleType === 'worker';
  // Pharmacy managers render PharmacyDashboard (not NewManagerDashboard) — no fit-manager chrome
  const fitManager =
    fitShell && roleType === 'manager' && options.businessTypeId !== 'pharmacie';
  const documentScroll =
    !kioskShell && DOCUMENT_SCROLL_PREFIXES.some((p) => matchesPrefix(path, p));
  const onSettings =
    matchesPrefix(path, '/settings') || matchesPrefix(path, '/mitarbeiter');
  const contextToolbarOwnsTitle =
    !kioskShell &&
    CONTEXT_TOOLBAR_TITLE_PREFIXES.some((p) => matchesPrefix(path, p));

  return {
    kioskShell,
    fitShell,
    fitPos,
    fitManager,
    documentScroll,
    onSettings,
    contextToolbarOwnsTitle,
    scroll: kioskShell
      ? 'viewport'
      : fitShell
        ? 'viewport'
        : documentScroll
          ? 'document'
          : 'default',
  };
}

export function resolveAdminLayoutMeta() {
  return {
    kioskShell: false,
    fitShell: false,
    fitPos: false,
    fitManager: false,
    documentScroll: true,
    onSettings: false,
    contextToolbarOwnsTitle: true,
    scroll: 'document',
  };
}

/** @param {ReturnType<typeof resolveRouteLayoutMeta>} meta */
export function buildMainContentClassName(meta) {
  return [
    'main-content',
    meta.kioskShell ? 'main-content--kiosk' : '',
    meta.fitShell ? 'main-content--fit-shell' : '',
    meta.fitPos ? 'main-content--fit-pos' : '',
    meta.fitManager ? 'main-content--fit-manager' : '',
    meta.documentScroll ? 'main-content--document-scroll' : '',
    meta.onSettings ? 'main-content--settings' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** @param {ReturnType<typeof resolveRouteLayoutMeta>} meta */
export function buildPageContentClassName(meta) {
  return [
    'page-content',
    meta.kioskShell ? 'page-content--kiosk' : '',
    meta.fitShell ? 'page-content--fit-shell' : '',
    meta.fitPos ? 'page-content--fit-pos' : '',
    meta.fitManager ? 'page-content--fit-manager' : '',
    meta.documentScroll ? 'page-content--document-scroll' : '',
  ]
    .filter(Boolean)
    .join(' ');
}
